<template>
  <Teleport v-if="isMobileView" to="body">
    <div
        ref="stickyRef"
        v-if="showAutocompletePopup && showButton"
        id="more-results-sticky"
        class="more-results__container q-d-grid q-gap-2 q-col-12 q-mt-2 q-mx-auto">
      <a v-if="showButton"
         class="q-btn q-btn-danger more-results__link"
         tabindex="-1" role="button"
         @click.prevent="handleSeeMoreClick">
        {{ SmartSearchTranslations.autocomplete.seeMore }}
        <span v-show="popupStore.getTotalProducts"> ({{ popupStore.getTotalProducts }})</span>
      </a>
    </div>
  </Teleport>
  <div
      v-else
      ref="stickyRef"
      v-show="showButton"
      id="more-results-sticky"
      class="more-results__container q-d-grid q-gap-2 q-col-12 q-mt-2 q-mx-auto">
    <a v-if="showButton"
       class="q-btn q-btn-danger more-results__link"
       tabindex="-1" role="button"
       @click.prevent="handleSeeMoreClick">
      {{ SmartSearchTranslations.autocomplete.seeMore }}
      <span v-show="popupStore.getTotalProducts"> ({{ popupStore.getTotalProducts }})</span>
    </a>
  </div>
</template>


<script setup lang="ts">
import { useSearchQueryStore } from "@/store/SearchQueryStore";
import { useUrlStore } from "@/store/SerpPage/UrlStore";
import { usePopupStore } from "@/store/AutocompletePopup/PopupStore";
import { useMobileViewStore } from "@/store/MobileViewStore";
import { storeToRefs } from "pinia";
import { inject, onUnmounted, ref, watch, computed } from "vue";
import { SmartSearchTranslationsInterface } from "@/core/types";

const SmartSearchConfig = inject("SmartSearchConfig").value;
const urlStore = useUrlStore();
const popupStore = usePopupStore();
const mobileViewStore = useMobileViewStore();

const searchQueryStore = useSearchQueryStore();
const { getSearchQuery } = storeToRefs(searchQueryStore);
const { hasAnyResults, showAutocompletePopup, showResults, loading } = storeToRefs(popupStore);
const { isMobileView } = storeToRefs(mobileViewStore);
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const SmartSearchTranslations = inject<SmartSearchTranslationsInterface>("SmartSearchTranslations")!;
const stickyRef = ref<HTMLElement | null>(null);
let unregisterStickySafe: (() => void) | null = null;

watch(stickyRef, (element) => {
  unregisterStickySafe?.();
  if (element) {
    unregisterStickySafe = popupStore.registerSafeElement(element);
  }
});

onUnmounted(() => {
  unregisterStickySafe?.();
});

const currentQuery = computed(() => (getSearchQuery.value || "").trim());

const showButton = computed(() =>
    showResults.value &&
    !loading.value &&
    hasAnyResults.value &&
    currentQuery.value.length >= 3
);

const handleSeeMoreClick = () => {
  const rawQuery = (currentQuery.value || popupStore.lastQuery || "").trim();
  if (!rawQuery) return;

  urlStore.goToSerpPage(
    SmartSearchConfig.appConfiguration.redirectionUrl,
    rawQuery
  );
};
</script>

<style>

/* ============ MOBILE ONLY ============ */
@media (max-width: 991.98px){

  /* Pasek CTA na stałe u dołu, tylko gdy widok aktywny */
  .q-autocomplete-active #more-results-sticky{
    position: fixed !important;
    left: 0; right: 0;
    /* offset od dołu = safe-area + kompensacja klawiatury + margines */
    bottom: calc(env(safe-area-inset-bottom) + var(--kb-offset, 0px) + 12px);
    z-index: 1000000 !important; /* nad listą, pod top-barem inputu */
    padding: 0 16px;   /* wewnętrzny gutter po bokach */
    pointer-events: none; /* pozwala by klik był tylko na przycisku */
  }

  .q-autocomplete-active #more-results-sticky .more-results__link{
    display: block;
    width: 100%;
    min-height: 36px;
    line-height: 36px;        /* prosto: tekst wyśrodkuj pionowo */
    text-align: center;
    border-radius: 12px;
    font-weight: 600;
    pointer-events: auto;      /* klik działa na samym przycisku */
    /* Twój czerwony styl już jest (q-btn-danger), zostawiamy */
    box-shadow: 0 6px 18px rgba(229,57,53,.28);
  }

  /* Żeby lista wyników nie wchodziła pod przycisk:
     dorzucamy padding-bottom do obszaru scrollowanego */
  .q-autocomplete-active .smartsearch-autocomplete__popup-container{
    padding-bottom: calc(
        env(safe-area-inset-bottom) + var(--bottom-bar-h, 64px) + 20px
    ) !important;
  }
}


@-webkit-keyframes q-spinner-border {
  to {
    transform: rotate(360deg) /* rtl:ignore */;
  }
}

@keyframes q-spinner-border {
  to {
    transform: rotate(360deg) /* rtl:ignore */;
  }
}
.q-spinner-border {
  display: inline-block;
  width: 2em;
  height: 2em;
  vertical-align: -0.125em;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  -webkit-animation: 0.75s linear infinite q-spinner-border;
  animation: 0.75s linear infinite q-spinner-border;
}

.q-spinner-border-sm {
  width: 1em;
  height: 1em;
  border-width: 0.2em;
}

@-webkit-keyframes q-spinner-grow {
  0% {
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: none;
  }
}

@keyframes q-spinner-grow {
  0% {
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: none;
  }
}
.q-spinner-grow {
  display: inline-block;
  width: 2em;
  height: 2em;
  vertical-align: -0.125em;
  background-color: currentColor;
  border-radius: 50%;
  opacity: 0;
  -webkit-animation: 0.75s linear infinite spinner-grow;
  animation: 0.75s linear infinite spinner-grow;
}

.q-spinner-grow-sm {
  width: 1em;
  height: 1em;
}

@media (prefers-reduced-motion: reduce) {
  .q-spinner-border,
  .q-spinner-grow {
    -webkit-animation-duration: 1.5s;
    animation-duration: 1.5s;
  }
}
</style>
